import { useEffect, useMemo, useState } from 'react';
import { chunkOfId } from '@versesverse/core';
import type { NodeRefsArtifact, VerseChunkArtifact } from '@versesverse/schema/artifacts';
import { buildPassages } from './passages';
import { makeTextOf } from './load';
import type { Passage, PassageTables } from './passages';

/*
 * OPENING A FIGURE'S PASSAGES, ON DEMAND.
 *
 * The verses do not travel in the first download — they arrive in ranges when a reader asks. So
 * tapping into a figure runs two fetches: its per-corpus name lists say WHICH verses name it, and
 * the ranges holding those verses carry the text. Then the pure assembler (passages.ts) shapes
 * them. A range that gives up (core's fetch has its own 12s timeout) does not block the rest — the
 * passages that loaded still show, and `incomplete` lets the panel offer the public site for the
 * ones that did not. Content and tables are stable per library, so this re-runs only on the node.
 */
export interface VerseSource {
  nodeRefs(corpus: string): Promise<NodeRefsArtifact>;
  verseChunk(chunk: number): Promise<VerseChunkArtifact>;
}

export interface VerseNode {
  id: string;
  corpora: string[];
}

export interface VerseTables extends PassageTables {
  chunkStarts: number[];
}

export interface VersesState {
  status: 'idle' | 'loading' | 'ready';
  passages: Passage[];
  /** A range did not arrive — the reader is offered somewhere else to read those. */
  incomplete: boolean;
  /**
   * HOW MANY ARE COMING, while they are still coming.
   *
   * The ids are known from `refs/*.json` — 330 kB, and already fetched — long before the verse
   * ranges that hold their text. Measured on the gate fixture: the ids land at ~343ms and the first
   * passage renders at 4.1 to 5.4 seconds, because every range holding any of them is awaited
   * first. A reader watched `Opening the passages…` for all of it, with no way to tell a big figure
   * from a stalled one.
   *
   * The same bargain the offline download strikes, and for the reason recorded there: the count
   * already existed and was simply held until the end.
   */
  expected: number;
}

interface Loaded {
  forId: string;
  ids: number[];
  chunks: Map<number, VerseChunkArtifact>;
  incomplete: boolean;
}

export function useNodeVerses(source: VerseSource, tables: VerseTables, node: VerseNode | null): VersesState {
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  /* Published before the ranges are asked for, and cleared with the node — see `expected`. */
  const [expected, setExpected] = useState<{ forId: string; count: number } | null>(null);

  useEffect(() => {
    if (!node) { setLoaded(null); return; }
    let live = true;
    setLoaded(null); // clear the previous node's verses so the panel shows loading, not stale text
    setExpected(null);
    const { id: nodeId, corpora } = node;

    void (async () => {
      /* 1. Which verses name this figure, from the per-corpus lists. A list that fails to load is
         skipped, not fatal — the others still answer. */
      const lists = await Promise.all(corpora.map(c => source.nodeRefs(c).catch(() => null)));
      const idSet = new Set<number>();
      for (const list of lists) {
        for (const id of list?.refs[nodeId] ?? []) idSet.add(id);
      }
      const ids = [...idSet].sort((a, b) => a - b);
      /* Said now, not after the ranges: this is the whole of what makes the wait legible. */
      if (live) setExpected({ forId: nodeId, count: ids.length });

      /* 2. The ranges that hold them, each fetched once. A range that gives up marks the result
         incomplete but does not sink the passages that did arrive. */
      const need = [...new Set(ids.map(id => chunkOfId(tables.chunkStarts, id)))];
      const chunks = new Map<number, VerseChunkArtifact>();
      let incomplete = false;
      await Promise.all(need.map(async chunk => {
        try { chunks.set(chunk, await source.verseChunk(chunk)); } catch { incomplete = true; }
      }));

      if (live) setLoaded({ forId: nodeId, ids, chunks, incomplete });
    })();

    return () => { live = false; };
  }, [node?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- source/tables stable per library

  return useMemo<VersesState>(() => {
    if (!node) return { status: 'idle', passages: [], incomplete: false, expected: 0 };
    if (!loaded || loaded.forId !== node.id) {
      /* Only this node's count: a stale one from the figure before would be a number about somebody else. */
      return { status: 'loading', passages: [], incomplete: false, expected: expected?.forId === node.id ? expected.count : 0 };
    }
    const textOf = makeTextOf(loaded.chunks, tables.chunkStarts);
    return { status: 'ready', passages: buildPassages(loaded.ids, textOf, tables), incomplete: loaded.incomplete, expected: loaded.ids.length };
  /* `expected` is a dependency, or the memo keeps the state it built before the count arrived and
     the reader is told nothing for the whole wait — which is what happened on the first attempt. */
  }, [node?.id, loaded, tables, expected]);
}
